<template>
  <div class="handover-takeback">
    <h6
      v-if="loading"
      class="font-dark-gray"
    >
      <i class="rotation fa fa-refresh" />
      {{ $t('loading') }}
    </h6>
    <div v-show="hotbs && hotbs.length > 0">
      <div class="handover-takeback__title">
        {{ $t('handoverTakeback') }}
      </div>
      <div class="handover-takeback__table">
        <table class="sort_table">
          <thead>
            <tr class="handover-takeback__table--header">
              <th
                v-for="header in tableHeaders"
                :key="header.name"
                :class="header.className"
                @click="sortTable(header)"
              >
                <span>
                  {{ header.name }}
                  <i
                    v-if="sortingAttr === header.attr"
                    :class="[reverse ? 'triangle-down' : 'triangle-up']"
                  />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="hotb in sortedHotbs"
              :key="hotb.id"
              class="handover-takeback__table--body"
            >
              <td>{{ hotb.companyName || emptyValue }}</td>
              <td>{{ hotb.tradeName || emptyValue }}</td>
              <td>{{ hotb.type | customTProjectTypeLabel }}</td>
              <td>{{ hotb.building || emptyValue }}</td>
              <td>
                <a
                  class="link_text"
                  @click.prevent="navToDetail(hotb.id)"
                >
                  {{ hotb.premises || emptyValue }}
                </a>
              </td>
              <td>{{ hotb.status | customStatusLabel }}</td>
              <td>{{ dateUtil.format(hotb.scheduledTransactionDate, 'DD MMM YY, HH:mm') || emptyValue }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import navigation from '@/utils/navigation'
import { list } from '@/api/hotb/request'
import dateUtil from '@/utils/dateUtil'
import { mapGetters, mapActions } from 'vuex'
import { aclHelper, tenantPermsTypes } from '@/utils/aclHelper'
import lodash from 'lodash'
import { getHotbCompanyName, getHotbTradeName, getHotbPremises } from '@/modules/hotb/utils'

export default {
  name: 'TenantDashboardHandoverTakeback',
  props: {
    preload: {
      type: Boolean,
      default: false,
    },
    preloadedHotb: {
      type: Array,
      default: undefined,
    },
  },
  data () {
    return {
      hotbs: [],
      emptyValue: '--',
      loading: true,
      sortingAttr: 'scheduledTransactionDate',
      reverse: false,
      dateUtil,
    }
  },
  computed: {
    ...mapGetters('user', ['isTenant']),
    tableHeaders () {
      return [
        {
          name: this.$t('company.basic.companyName'),
          className: 'companyName',
          attr: 'companyName',
        },
        {
          name: this.$t('company.tenancyRecord.tradeName'),
          className: 'tradeName',
          attr: 'tradeName',
        },
        {
          name: this.$t('tenancy.type'),
          className: 'type',
          attr: 'type',
        },
        {
          name: this.$t('tenancy.building'),
          className: 'building',
          attr: 'building',
        },
        {
          name: this.$t('handover.premises'),
          className: 'premises',
          attr: 'premises',
        },
        {
          name: this.$t('status.name'),
          className: 'status',
          attr: 'status',
        },
        {
          name: this.$t('scheduledOn'),
          className: 'date',
          attr: 'scheduledTransactionDate',
        },
      ]
    },
    sortedHotbs () {
      const rawList = this.hotbs.map((hotb) => {
        return {
          scheduledTransactionDate: lodash.get(hotb, 'scheduledTransactionDate'),
          type: lodash.get(hotb, 'type'),
          building: lodash.get(hotb, 'tenancy.building.fullName'),
          companyName: getHotbCompanyName(hotb),
          tradeName: getHotbTradeName(hotb),
          premises: getHotbPremises(hotb) || this.emptyValue,
          status: lodash.get(hotb, 'status'),
          id: lodash.get(hotb, 'id'),
        }
      })

      const result = lodash.orderBy(rawList, this.sortingAttr)
      return this.reverse ? result.reverse() : result
    },
  },
  mounted () {
    if (this.preload) {
      this.hotbs = this.preloadedHotb
      this.loading = false
    } else {
      this.getHotbs()
    }
  },
  methods: {
    ...mapActions('hotb', ['setLastDashboardDateFilterIndex']),
    navTo (name) {
      navigation.toPage(name)
    },
    /** Tenant dashboard widget: detail without Prev/Next / back-to-search (parity with engineer TMS dashboard widget). */
    navToDetail (id) {
      this.$store.dispatch('listNavigation/clearListNavigationSequence')
      if (this.isTenant) {
        navigation.toPage('tenantHandover', { id })
      } else {
        navigation.toPage('handover', { id })
      }
    },
    hasReadAccess () {
      return aclHelper.getAccess(tenantPermsTypes.hotb.readUpdate)
    },
    async getHotbs () {
      if (this.hasReadAccess()) {
        this.hotbs = await list(undefined, true) || []
        this.loading = false
      }
    },
    sortTable (header) {
      if (this.sortingAttr === header.attr) {
        this.reverse = !this.reverse
      } else {
        this.sortingAttr = header.attr
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~styles/animation";
@import '~styles/components/hotb-list';
.type {
  width: 8%;
}
.building {
  width: 15%;
}
.companyName {
  width: 22%;
}
.tradeName {
  width: 20%;
}
.premises {
  width: 14%;
}
.date {
  width: 11%;
}
.status {
  width: 10%;
  font-weight: bold;
}
</style>
